import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import postscribe from 'postscribe';
import { Col, Flex, Form, Row, Steps } from 'antd';
import Couriers from './Couriers';
import Products from './Products';
import Sensors from './Sensors';
import Users from './Users';
import CustomButton from '../../../components/CustomButton';
import { useToken } from '../../../common/helper';
import './Steps.scss';
import { useNavigate } from 'react-router-dom';
import LocationMap from './LocationsMap';
import NotificationSetup from './NotificationsSetup';

const steps = [
  { title: 'Transport', content: Couriers },
  { title: 'Products', content: Products },
  { title: 'Sensors', content: Sensors },
  { title: 'Locations', content: LocationMap },
  { title: 'Users', content: Users },
  { title: 'Notifications', content: NotificationSetup },
];

export const shouldLoadAmazonConnectWidget = () =>
  process.env.REACT_APP_DISABLE_EXTERNAL_WIDGETS !== 'true';

const OnboardingSteps = () => {
  const { decodedUserToken } = useToken();
  const decodedToken = decodedUserToken();

  const [current, setCurrent] = useState(0);
  const [form] = Form.useForm();
  const productsFormRef = useRef(null);
  const injectedRef = useRef(false);
  const footerRef = useRef(null);
  const contentRef = useRef(null);
  const [footerHeight, setFooterHeight] = useState(0);

  const navigate = useNavigate();

  const next = async () => {
    if (productsFormRef?.current?.completeStep) {
      const success = await productsFormRef.current.completeStep();
      if (!success) return;
    }
    if (current < steps.length - 1) setCurrent(current + 1);
    else navigate('/confirmation');
  };

  useEffect(() => {
    if (!shouldLoadAmazonConnectWidget()) return;
    if (injectedRef.current) return;

    const rootEl = document.getElementById('root');
    if (!rootEl) {
      console.error('Target element #root not found');
      return;
    }

    const name = decodedToken?.name || 'Guest';
    const company = decodedToken?.company_name || 'Unknown';

    const scriptContent = `
<script type="text/javascript">
  (function (w, d, x, id) {
    var s = d.createElement('script');
    s.src = 'https://zoomlogiconnect.my.connect.aws/connectwidget/static/amazon-connect-chat-interface-client.js';
    s.async = 1;
    s.id = id;
    d.getElementsByTagName('head')[0].appendChild(s);
    w[x] = w[x] || function () { (w[x].ac = w[x].ac || []).push(arguments); };
  })(window, document, 'amazon_connect', 'c10b159f-c584-4f49-8192-66738e4be615');

  amazon_connect('styles', {
    iconType: 'CHAT_VOICE',
    openChat:  { color: '#ffffff', backgroundColor: 'var(--light-blue)' },
    closeChat: { color: '#ffffff', backgroundColor: 'var(--light-blue)' },
    headerConfig: { headerMessage: 'Zoomlogi', headerColorHex: '#266ef1' },
    logoConfig:   { sourceUrl: '' }
  });

  amazon_connect('snippetId',
    'QVFJREFIZ2ZYaENvQWJCb1ZtYmxRNlFMNVJYMlhab3BKY1p0RnRGQ2pJNGhxdnBvemdIdEZVL2xnY0dRRzZrNjRPN2NlcnVVQUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNTmFPaUlFdXRWMkkwZUdUTEFnRVFnQ3RZZEZmdFdqcjd0RjNMczd4ZFY0bitWN1l2d1I2WXQ5azNsd3BMZW52K0pZc3dKT3hTNzJSTStNUnM6Om9oVElDQTBndVl6YXJKQlJVZ2pSRVNXUTZzS0UwT0tHUHR6N2xVaEhjaHVYNHhiUkE5UGs4Y1FtMGJ5NG1iK0gya2s2bnl5dlBhQmVVQkl5Wnhsd0xKOENld0lLQmVjZnByS2lVT3dIS0dreUNJNjJBN2hQUG5iV1pveXdnbHVWTUJzTnV0S0xZQ3NKdThmdklMMjRwa2JQK3dGcm9raz0='
  );

  // Identify the customer (Pre-contact flow can use either "company" or "companyname")
  amazon_connect('customerMetadata', {
    name: ${JSON.stringify(name)},
    company: ${JSON.stringify(company)},
    companyname: ${JSON.stringify(company)}
  });

  amazon_connect('supportedMessagingContentTypes', [
    'text/plain',
    'text/markdown',
    'application/vnd.amazonaws.connect.message.interactive',
    'application/vnd.amazonaws.connect.message.interactive.response'
  ]);

  // Embedded form — only Start Chat is visible
  amazon_connect('viewConfig', JSON.stringify({
    name: 'Contact Form',
    id: 'contact-form-embedded',
    arn: 'arn:aws:connect:us-east-1:050451381240:instance/3c1da07a-b3e7-45f0-b335-134d3d4b1a61/view/004f3a4d-78b1-4d0a-8351-0e52fbf20be7:$LATEST',
    type: 'CUSTOMER_MANAGED',
    status: 'PUBLISHED',
    content: {
      template: {
        Head: { Configuration: { Layout: { Columns: '12' } }, Title: 'Contact Form' },
        Body: [
          {
            _id: 'Form',
            Type: 'Form',
            Props: { HideBorder: false },
            Content: [
              {
                _id: 'FirstName',
                Type: 'FormInput',
                Props: {
                  Label: 'First name',
                  Name: 'first-name',
                  InputType: 'text',
                  Required: true,
                  DefaultValue: ${JSON.stringify(name)}
                },
                Content: []
              },
              {
                _id: 'Company',
                Type: 'FormInput',
                Props: {
                  Label: 'Company',
                  Name: 'company',
                  InputType: 'text',
                  Required: true,
                  DefaultValue: ${JSON.stringify(company)}
                },
                Content: []
              },
              {
                _id: 'StartChat',
                Type: 'ConnectAction',
                Props: {
                  ConnectActionType: 'StartChatContact',
                  Label: 'Start Chat',
                  StartChatContactProps: {
                    ContactFlowId: 'arn:aws:connect:us-east-1:050451381240:instance/3c1da07a-b3e7-45f0-b335-134d3d4b1a61/contact-flow/67d5abcb-0f82-4f18-bf40-0e9b0185b591',
                    ChatFields: {
                      CustomerDisplayName: { _linkedFormOutputName: 'first-name' },
                      InitialMessage:      { _linkedFormOutputName: 'company' }
                    },
                    Attributes: [
                      { Key: 'name',        Value: { _linkedFormOutputName: 'first-name' } },
                      { Key: 'company',     Value: { _linkedFormOutputName: 'company' } },
                      { Key: 'companyname', Value: { _linkedFormOutputName: 'company' } }
                    ]
                  }
                },
                Content: ['Connect Action Button']
              }
            ]
          }
        ]
      },
      actions: ['ActionSelected'],
      inputSchema: { type: 'object', properties: {}, required: [] }
    }
  }));
</script>
    `;

    postscribe(rootEl, scriptContent);
    injectedRef.current = true;
  }, [decodedToken?.name, decodedToken?.company_name]);

  const incrementCurrent = () => {
    if (current < steps.length - 1) setCurrent(current + 1);
    else navigate('/confirmation');
  };

  const prev = () => {
    if (current > 0) setCurrent(current - 1);
  };

  const handleSkipButtonClick = () => {
    if (current < steps.length - 1) setCurrent(current + 1);
    else navigate('/shipment');
  };

  useLayoutEffect(() => {
    const footer = footerRef.current;
    if (!footer) return undefined;

    const updateFooterHeight = () => {
      setFooterHeight(Math.ceil(footer.getBoundingClientRect().height));
    };

    updateFooterHeight();

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateFooterHeight)
        : null;

    resizeObserver?.observe(footer);
    window.addEventListener('resize', updateFooterHeight);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateFooterHeight);
    };
  }, [current]);

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, left: 0 });
  }, [current]);

  return (
    <div
      className='onboarding-steps-shell'
      style={
        footerHeight
          ? { '--onboarding-footer-height': `${footerHeight}px` }
          : undefined
      }
    >
      <div className='onboarding-steps-content-container'>
        <div className='onboarding-steps-content' ref={contentRef}>
          <div className='onboarding-steps-width-wrapper'>
            <div className='onboarding-steps-current' key={current}>
              {current === 1 ? (
                <Products
                  onNext={next}
                  ref={productsFormRef}
                  onSuccess={incrementCurrent}
                />
              ) : current === 4 ? (
                <Users ref={productsFormRef} onSuccess={incrementCurrent} />
              ) : current === 3 ? (
                <LocationMap
                  ref={productsFormRef}
                  onSuccess={incrementCurrent}
                />
              ) : current === 5 ? (
                <NotificationSetup
                  form={form}
                  onNext={next}
                  ref={productsFormRef}
                  onSuccess={incrementCurrent}
                />
              ) : current === 0 ? (
                <Couriers />
              ) : (
                <Sensors />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='custom-steps-container' ref={footerRef}>
        <Flex
          className='custom-steps'
          justify='space-between'
          align='center'
          gap='large'
          style={{ height: 'inherit' }}
        >
          <CustomButton
            onClick={current > 0 ? prev : undefined}
            text='Back'
            width='72px'
            height='48px'
            color='var(--text-primary)'
            fontSize='16px'
            bgColor='var(--light-gray)'
            fontWeight='500'
            boxShadow='none'
            className={`back-btn ${current === 0 ? 'footer-placeholder-btn' : ''}`}
          />

          <Row style={{ flex: 1 }}>
            <Col span={24}>
              <Steps
                current={current}
                titlePlacement='vertical'
                responsive={false}
                items={steps.map((step) => ({ title: step.title }))}
              />
            </Col>
          </Row>

          <div className='steps-action'>
            <Flex gap={12}>
              <CustomButton
                onClick={current > 0 ? prev : undefined}
                text='Back'
                width='72px'
                height='48px'
                color='var(--text-primary)'
                fontSize='16px'
                bgColor='var(--light-gray)'
                fontWeight='500'
                boxShadow='none'
                className={`mobile-back-btn ${current === 0 ? 'footer-placeholder-btn' : ''}`}
              />
              <CustomButton
                onClick={handleSkipButtonClick}
                text='Skip for now'
                width='125px'
                height='48px'
                color='var(--text-primary)'
                fontSize='16px'
                bgColor='inherit'
                fontWeight='500'
                boxShadow='none'
                className='skip-btn'
              />
              <CustomButton
                onClick={next}
                text='Next'
                width='223px'
                height='48px'
                color='#ffffff'
                fontSize='16px'
                bgColor='#000000'
                fontWeight='500'
                boxShadow='none'
                className='next-btn'
              />
            </Flex>
          </div>
        </Flex>
      </div>
    </div>
  );
};

export default OnboardingSteps;
